/* eslint-disable ember/no-classic-components, ember/no-computed-properties-in-native-classes */
import Component from '@ember/component';
import { action, computed } from '@ember/object';
import {
  rejectBy,
  sortBy,
} from '@linc-technologies/linced-common/utils/arrays';
import { tagName } from '@ember-decorators/component';
import { barSegments } from 'linced-app/utils/progress-overview';

/**
 * Insights::MilestoneDistribution shows how learners are spread across the progress
 * levels at each milestone, as a 100% stacked bar per milestone.
 *
 * It reads the same rows the "All Students" table renders rather than counting
 * again, so the two can never disagree.
 *
 * @param {string} title - the card's title; "Distribution by milestone".
 * @param {string} dataId - the card's screenshot target; unique per chart.
 * @param {string} cohort - 'all' or 'startedHere', handed back with each
 *   selection so the drilldown lists the same students the chart counts.
 * @param {Ember.Array} data - the count rows, as built by the progress
 *   overview controller (judgement rows plus the total/percentage summaries).
 * @param {Array<string>} displayYears - academic year labels, oldest first.
 * @param {{mid: string, end: string}} labels - what the two slots of a year
 *   are called in the chart's mode.
 */
@tagName('')
export default class MilestoneDistribution extends Component {
  /** @type {Ember.Array|null} */
  data = null;

  /** @type {Array<string>|null} */
  displayYears = null;

  /** @type {{mid: string, end: string}|null} */
  labels = null;

  /** @type {Function} Called with (year, half, judgement) on a segment click. */
  onSelect() {}

  /** @type {string} */
  title = 'Distribution by milestone: all students';

  /** @type {string} */
  dataId = 'milestone_distribution';

  /** @type {string} */
  cohort = 'all';

  // Lowest level first, so both the legend and each bar read left to right the
  // way the levels do.
  @computed('data.[]')
  get judgementRows() {
    let data = this.data || [];
    return sortBy(rejectBy(data, 'isSummary'), 'key');
  }

  @computed('judgementRows.[]')
  get legend() {
    return this.judgementRows.map((row) => ({
      label: row.label,
      swatchStyle: row.swatchStyle,
    }));
  }

  // One column per milestone, most recent at the top: end then mid within
  // each year, years newest first - the reverse of the table above.
  @computed('judgementRows.[]', 'displayYears.[]', 'labels.{mid,end}')
  get columns() {
    return (this.displayYears || [])
      .reduce((columns, year) => {
        ['mid', 'end'].forEach((half) => {
          columns.push(this._column(year, half));
        });
        return columns;
      }, [])
      .reverse();
  }

  _column(year, half) {
    let counts = this.judgementRows.map((row) => ({
      judgement: row.key,
      label: row.label,
      count: row.data[year][half],
      colour: row.colour,
      textColour: row.textColour,
    }));
    let total = counts.reduce((sum, entry) => sum + entry.count, 0);

    return {
      year,
      half,
      label: `${this.labels?.[half]} ${year}`,
      total,
      // A milestone nobody has been marked against leaves an empty track
      // rather than a bar of zero-width segments.
      // Each segment is labelled as the "All students by descriptor" bars
      // are: its descriptor and count where it is wide enough, the count
      // alone where it is not.
      segments: barSegments(
        counts.filter((entry) => entry.count > 0),
        total,
      ),
    };
  }

  // The drilldown belongs to the page, so the tables and these bars open the
  // same panel.
  @action
  showSegment(column, judgement) {
    this.onSelect(column.year, column.half, judgement, this.cohort);
  }
}
